import { getDataWithDeduplication } from '@componentAPIs';
import {
  isStaleKpiRequestError,
  logEnforcedDR,
  parseIBKPIsResponse,
  type IBKPIsResponse
} from '../util/enforcedDynamicRouting';

export type ApiResponse<T = unknown> = {
  status?: number;
  data?: T;
};

type IBPlanningStoreLike = {
  selectedRouteNameList: string[];
  updateKPIData: (kpiData: unknown[]) => void;
  updateIsFirstOptimizationRun: (value: boolean) => void;
};

type ApplyGetIBKPIsResponseArgs = {
  response: IBKPIsResponse;
  IBPlanningStore: IBPlanningStoreLike;
  isDynamicRoutingCompleted: boolean;
  getCityRoutesForDynamicRouting: () => string[];
  updateSelectedRoutesInStore: (cityRoutes: string[], selectedRoutesList: string[]) => void;
};

type GetIBKPIsParams = Omit<ApplyGetIBKPIsResponseArgs, 'response'> & {
  scenarioID?: string | number | null;
};

export const handleStaleDedupOrFallback = (error: unknown, onFallback: () => void): void => {
  if (isStaleKpiRequestError(error)) {
    return;
  }
  onFallback();
};

export const clearIBKPIsState = (IBPlanningStore: IBPlanningStoreLike): void => {
  IBPlanningStore.updateKPIData([]);
  IBPlanningStore.updateIsFirstOptimizationRun(false);
};

export const applyGetIBKPIsResponse = ({
  response,
  IBPlanningStore,
  isDynamicRoutingCompleted,
  getCityRoutesForDynamicRouting,
  updateSelectedRoutesInStore
}: ApplyGetIBKPIsResponseArgs): void => {
  const parsed = parseIBKPIsResponse(response);
  IBPlanningStore.updateKPIData(parsed.ibKpi);
  if (isDynamicRoutingCompleted) {
    IBPlanningStore.updateIsFirstOptimizationRun(false);
    logEnforcedDR('GetIBKPIs', {
      ok: parsed.ok,
      isFirstOptimizationRun: false,
      forcedFalseAfterLocalDR: true
    });
    return;
  }
  IBPlanningStore.updateIsFirstOptimizationRun(parsed.isFirstOptimizationRun);
  logEnforcedDR('GetIBKPIs', {
    ok: parsed.ok,
    isFirstOptimizationRun: parsed.isFirstOptimizationRun
  });
  if (parsed.ok && parsed.isFirstOptimizationRun) {
    const cityRoutes = getCityRoutesForDynamicRouting();
    updateSelectedRoutesInStore(cityRoutes, IBPlanningStore.selectedRouteNameList);
  }
};

export const handleGetIBKPIsError = (
  error: unknown,
  IBPlanningStore: IBPlanningStoreLike
): void => {
  handleStaleDedupOrFallback(error, () => clearIBKPIsState(IBPlanningStore));
};

export const fetchAndApplyIBKPIs = ({
  scenarioID,
  IBPlanningStore,
  isDynamicRoutingCompleted,
  getCityRoutesForDynamicRouting,
  updateSelectedRoutesInStore
}: GetIBKPIsParams): Promise<void> => {
  if (!scenarioID) {
    clearIBKPIsState(IBPlanningStore);
    return Promise.resolve();
  }
  return getDataWithDeduplication('IBKPIs', `scenarioId=${scenarioID}`).then(
    (response: IBKPIsResponse) => {
      applyGetIBKPIsResponse({
        response,
        IBPlanningStore,
        isDynamicRoutingCompleted,
        getCityRoutesForDynamicRouting,
        updateSelectedRoutesInStore
      });
    },
    (error: unknown) => {
      handleGetIBKPIsError(error, IBPlanningStore);
    }
  );
};
